// ui/presenters/qualificationRows.js
import { i18n } from 'boot/i18n';
import { formatCalendarDate } from 'src/utils/DateUtils';

import { isInGoodStanding, isResolvable } from '../../domain/model/Credential';

const KEY = 'staff.qualifications';

/**
 * Turns a person's stored qualifications into the rows the panel renders.
 *
 * OUT HERE RATHER THAN IN THE PANEL so the list behavior can be tested without mounting a form, and
 * because what a row SAYS is the part most likely to be wrong in a way no type catches.
 *
 * CURRENT AND PAST ARE SEPARATE GROUPS, following `ClientCoveragePanel` one context over, which
 * solved this exact problem for records carrying validity windows: past ones are retained because a
 * date of service inside their window still resolves through them, but they take no part in what is
 * true today. Membership is DERIVED from the dates, so a credential moves between the groups on its
 * own as reality moves, and nothing has to be marked expired by hand.
 *
 * A PAST ROW CARRIES NO "EXPIRED" LABEL. The section header above it already says so, and repeating
 * it on every row is noise — the same reasoning `EndedCoverageList` records for its own rows.
 */

/** A credential that cannot say when it started, which is every row stored before this context. */
const isIncomplete = (credential) => !isResolvable(credential);

/**
 * What a credential row says under its name.
 *
 * An incomplete row says what it needs rather than what it is, because that is the only thing anyone
 * can act on: without a start date it resolves to nothing and bills nothing, whatever else it holds.
 */
export function credentialCaption(credential) {
  if (isIncomplete(credential)) {
    return i18n.t(`${KEY}.captionNeedsStartDate`);
  }

  // Said before the window, because a suspended credential's dates read as perfectly open: without
  // this the row is indistinguishable from one that is fine, and the only visible difference would
  // be a claim that quietly stops resolving.
  if (!isInGoodStanding(credential)) {
    return i18n.t(`${KEY}.captionNotInGoodStanding`, {
      standing: i18n.t(`${KEY}.standings.${credential.status}`),
    });
  }

  const from = formatCalendarDate(credential.startDate);

  return credential.expirationDate === null
    ? i18n.t(`${KEY}.captionFromNoExpiry`, { from })
    : i18n.t(`${KEY}.captionWindow`, { from, to: formatCalendarDate(credential.expirationDate) });
} // credentialCaption()

/** What a degree row says under its level: the field of study, and where it was awarded. */
export function degreeCaption(degree) {
  const parts = [degree?.fieldOfStudy, degree?.school].filter(Boolean);

  return parts.length === 0 ? null : parts.join(' · ');
} // degreeCaption()

/**
 * The credentials split into what has ENDED and everything else.
 *
 * ENDED, NOT "NOT VALID TODAY", and the difference is a whole third state. A credential whose window
 * has not started yet is forthcoming, not history: renewing before the current one lapses is the
 * ordinary way this is done, and the paper file shows the same certificate twice with the next
 * window already recorded. Filing that under "expired" says the opposite of what is true.
 *
 * So the split is the one `ClientCoveragePanel` makes: a row leaves the main group only once its end
 * date has passed, and current and forthcoming sit together where the caption tells them apart.
 *
 * INCOMPLETE ROWS STAY IN THE MAIN GROUP too. A row nobody has finished is work outstanding, and
 * burying it under a collapsed history is how it stays unfinished: it belongs where somebody will
 * see it, saying what it needs.
 *
 * @param {Array<Object>} credentials
 * @param {string} asOf - 'YYYY-MM-DD'
 */
export function splitCredentials(credentials, asOf) {
  return (credentials ?? []).reduce((groups, credential) => {
    const hasEnded = !isIncomplete(credential)
      && typeof credential.expirationDate === 'string'
      && credential.expirationDate < asOf;

    groups[hasEnded ? 'ended' : 'current'].push(credential);
    return groups;
  }, { current: [], ended: [] });
} // splitCredentials()
